import { Form } from "@base-ui/react/form";
import { Controller, useForm, useWatch } from "react-hook-form";
import { EntityStatus } from "@repo/types/enums";
import { KeyValuePairType, TimelineActivityType } from "../../../types/enums";
import type { AddUpdateKeyValuePairViewModel } from "../../../types/viewModels/AddUpdateKeyValuePairViewModel";
import type { ExecutionResponse } from "@repo/types/responses";
import useAddUpdateKeyValuePair from "../../../hooks/queries/useKeyValuePairs";
import { useDialog } from "../../../hooks/contexts/useDialog";
import { CustomIconKeyValuePairs } from "../../../data/icons";
import { FormField } from "../../form/input/FormField";
import { FormInput } from "../../form/input/FormInput";
import Button from "../../ui/button/Button";

interface AddEditActivityProps {
  onClose: () => void;
}

interface IconGridProps {
  value?: string;
  bgColor?: string;
  textColor?: string;
  onChange: (id: string) => void;
}

function IconGrid({ value, bgColor, textColor, onChange }: IconGridProps) {
  return (
    <div className="grid grid-cols-5 gap-2 max-h-40 overflow-y-auto">
      {CustomIconKeyValuePairs.map(({ id, icon: Icon }) => {
        const isSelected = value === id;

        return (
          <button
            type="button"
            onClick={() => onChange(id)}
            className={[
              "w-9 h-8 rounded-md flex items-center justify-center border transition",
              isSelected
                ? "ring-2 ring-blue-500 border-transparent"
                : "border-gray-300 hover:border-gray-400",
            ].join(" ")}
            style={{ backgroundColor: bgColor, color: textColor }}
          >
            <Icon size={14} />
          </button>
        );
      })}
    </div>
  );
}

export function AddEditActivity({ onClose }: AddEditActivityProps) {
  const dialog = useDialog();
  const { mutateAsync: updateKeyValuePair } = useAddUpdateKeyValuePair(
    KeyValuePairType.TimelineActivityType,
  );

  const { control, handleSubmit } = useForm<AddUpdateKeyValuePairViewModel>({
    defaultValues: {
      PairType: KeyValuePairType.TimelineActivityType,
      PairTypeEnumValue: TimelineActivityType.Custom,
      Status: EntityStatus.Active,
    },
  });

  const bgColor = useWatch({
    control,
    name: "BgColorHex",
  });

  const textColor = useWatch({
    control,
    name: "TextColorHex",
  });

  const handleAddActivityType = async (
    model: AddUpdateKeyValuePairViewModel,
  ): Promise<ExecutionResponse> => {
    const res = await updateKeyValuePair(model);

    if (res) {
      dialog.show({
        title: "Add custom activity",
        message: res.Message,
        type: res.Result ? "success" : "error",
        confirmText: "Ok",
      });
      onClose();
    }

    return res;
  };

  return (
    <div className="p-3">
      <div className="text-sm text-black font-semibold mb-2">
        Add custom activity
      </div>

      <Form>
        <div className="grid grid-cols-1 gap-2">
          <div className="">
            <Controller
              control={control}
              name="Value"
              rules={{ required: "Name is required" }}
              render={({ field, fieldState }) => (
                <FormField error={fieldState.error?.message}>
                  <FormInput {...field} />
                </FormField>
              )}
            />
          </div>

          <div className="">
            <Controller
              control={control}
              name="BgColorHex"
              render={({ field, fieldState }) => (
                <FormField
                  label="Background color"
                  error={fieldState.error?.message}
                >
                  <FormInput type="color" {...field} />
                </FormField>
              )}
            />
          </div>

          <div className="">
            <Controller
              control={control}
              name="TextColorHex"
              render={({ field, fieldState }) => (
                <FormField label="Text color" error={fieldState.error?.message}>
                  <FormInput type="color" {...field} />
                </FormField>
              )}
            />
          </div>

          <div className="">
            <Controller
              control={control}
              name="Icon"
              render={({ field }) => (
                <FormField label="Icon">
                  <IconGrid
                    bgColor={bgColor}
                    textColor={textColor}
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormField>
              )}
            />
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <Button
            type="submit"
            size="xs"
            onClick={handleSubmit(handleAddActivityType)}
          >
            Save activity
          </Button>
        </div>
      </Form>
    </div>
  );
}
